/*
 * The sidebar navigation model and the Phase-1 destination list.
 *
 * This file is the LOCKED F5-b WI-1.1 → WI-1.2 contract (F5-b Decomposition Plan § Shared Contracts):
 * F5-b WI-1.1 renders every entry unconditionally; F5-b WI-1.2 stacks on this branch and gates each entry
 * through the shipped PermissionGuard via the Sidebar `renderNavItem` seam. The coupling that
 * matters is the `permissionKeys` list on each entry (spec W-2; a list of keys per F3-a FR-V7) — each a
 * `<resource>.<action>` key the FS-6 catalog declares now or that a later feature declares as it lands
 * (AD #30), any one of which shows the entry. An undeclared or un-granted key is always-absent → an
 * entry none of whose keys is held is hidden (fail-closed, F5-b NFR-4), so referencing a
 * not-yet-declared key here is safe.
 */
import type { IconName } from '../ui';

/** A nav badge is either a running count (neutral tone) or an alert (`accent/gold`) — §6. */
export type NavBadgeKind = 'count' | 'alert';

/**
 * The optional badge a nav entry can carry (§6 "count badges — neutral tone for counts,
 * `accent/gold` for alerts"). Phase-1 `navConfig` sets none — there is no data layer yet — so this
 * is the ready-made attachment a later feature populates, either statically here or via a live
 * count a feature feeds through a Sidebar override.
 */
export interface NavBadge {
    readonly kind: NavBadgeKind;
    readonly value: number | string;
    /**
     * What the badge means, in words (`3 overdue`). Set, the number is hidden from assistive technology
     * and `, <label>` is read after the entry's label instead, so the link's name reads *Activities, 3
     * overdue*; the icon rail's tooltip says the same (F3-a spec AC-23; F3-a WI-2.2 plan Decision 21).
     */
    readonly label?: string;
}

/**
 * One primary sidebar destination.
 *
 * `permissionKeys` is the cross-feature coupling: the FS-6 `<resource>.<action>` keys that gate the
 * entry, any one of which suffices (F3-a FR-V7 — Activities shows for `account.view` or `lead.view`).
 * The non-empty tuple makes an entry with no key a type error. The sidebar, the route guard and the
 * landing each read it through `firstHeldPermission` or `some`, so they cannot disagree. `icon` is a
 * concept key from the registry, so the glyph can change without touching this list.
 */
export interface NavItem {
    readonly id: string;
    readonly label: string;
    readonly icon: IconName;
    readonly path: string;
    readonly permissionKeys: readonly [string, ...string[]];
    readonly badge?: NavBadge;
}

/**
 * The seven Phase-1 primary destinations, in landing-precedence order (F5-b FR-12 lands on the first
 * one with a held key). Each key is one of AD #34's catalog keys: Contacts, directly after Accounts,
 * gates on the same `account.view` (AD #34 has no `contact.*` key; F1-f FR-N1), Activities, directly
 * after Leads, on `account.view` or `lead.view` (F3-a FR-V7 — every role holding either reaches an
 * earlier entry first, so no landing moves), Email on `email.manage` (a user's own mailbox), Pricing on
 * `buying_standard.view` — the inclusive key every role that should see pricing holds — and Settings,
 * last, on any one of its pages' keys (AD #41) — Users, Roles, the three tags pages and the email templates
 * (design-system §8, Rev 70 and Rev 71; the seeded IT Admin holds them all). `email.manage`
 * alone does not show Settings. No Dashboard entry (F17 is Phase 2). Labels are Title Case (§9 — Title
 * Case for nav/screen names).
 */
export const navConfig = [
    {
        id: 'accounts',
        label: 'Accounts',
        icon: 'account',
        path: '/accounts',
        permissionKeys: ['account.view'],
    },
    {
        id: 'contacts',
        label: 'Contacts',
        icon: 'contact',
        path: '/contacts',
        permissionKeys: ['account.view'],
    },
    { id: 'leads', label: 'Leads', icon: 'lead', path: '/leads', permissionKeys: ['lead.view'] },
    {
        id: 'activities',
        label: 'Activities',
        icon: 'activity',
        path: '/activities',
        permissionKeys: ['account.view', 'lead.view'],
    },
    { id: 'email', label: 'Email', icon: 'email', path: '/email', permissionKeys: ['email.manage'] },
    {
        id: 'pricing',
        label: 'Pricing',
        icon: 'pricing',
        path: '/pricing',
        permissionKeys: ['buying_standard.view'],
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: 'settings',
        path: '/settings',
        // Every Settings page's key (AD #41), in the pages' order (`SETTINGS_PAGE_KEYS`); any one shows the entry.
        permissionKeys: [
            'settings.users.manage',
            'settings.roles.manage',
            'settings.account_tags.manage',
            'settings.contact_tags.manage',
            'settings.lead_tags.manage',
            'settings.email_templates.manage',
        ],
    },
] as const satisfies readonly NavItem[];

/** The id of one navigation entry — what `RoutePermissionGuard` takes to find a route's key. */
export type NavId = (typeof navConfig)[number]['id'];
